// ENG-6392 (epic ENG-6333 — graceful degradation): the manager-side memory that
// lets a safe auto-resume CLOSE THE LOOP.
//
// The whole-agent resume-health gate (ENG-6382) proves every dependency reads
// active again, but it deliberately can't name WHICH one recovered — a breaker
// trip's causes aren't 1:1 with integrations and there's no server-side
// trip→dependency link. The close-the-loop confirmation (ENG-6384) therefore
// needs the manager to supply the recovered `(kind,label)` pairs on the
// auto-resume request. This ledger is that memory: it folds the manager's own
// rolling connectivity-probe verdicts (ENG-5641) into a per-agent down→ok
// record and hands back the recovered set to POST as `recovered_dependencies`.
//
// It mirrors the server's escalation semantics exactly (see
// integration-connectivity-report.ts): a sustained `down` is what the central
// evaluator escalates into a manager "X is down" DM, and `ok` is its recovery
// signal — so we record on `down` and mark recovered on `ok`, ignoring the
// non-escalating `degraded` / `transient_error`. A dependency that goes `down`
// then disappears (deleted) never sees an `ok`, so it stays unrecovered and is
// simply dropped when the trip clears — never mis-reported as "back online".
//
// Over-reporting is safe by construction: the server confirms a `(kind,label)`
// only against an OPEN single-agent incident and skips the rest
// (resume-confirmation-notify.ts), so a stale entry costs at most one skipped
// log line, never a wrong DM.
//
// In-memory only (not persisted to manager-state.json): a manager restart loses
// the ledger and the next resume simply sends no `recovered_dependencies` — the
// incident then ages out via the digest path (ENG-6365). Graceful degradation,
// not a regression. The probes re-populate the ledger within an interval anyway.

/** integration vs channel — matches the original alert's incident kind. */
export type RecoveredDependencyKind = 'integration' | 'channel';

export interface RecoveredDependency {
  kind: RecoveredDependencyKind;
  /** `definition_id` (integration) / `channel_id` (channel) — the incident label. */
  label: string;
}

export interface ConnectivityVerdict {
  kind: RecoveredDependencyKind;
  label: string;
  /** Raw connectivity status: 'ok' | 'degraded' | 'transient_error' | 'down'. */
  status: string;
}

interface LedgerEntry {
  kind: RecoveredDependencyKind;
  label: string;
  /** True once a probe reported this dep `ok` after a prior `down`. */
  recovered: boolean;
}

function ledgerKey(kind: RecoveredDependencyKind, label: string): string {
  return `${kind}:${label}`;
}

export class DependencyRecoveryLedger {
  /** code_name → (`${kind}:${label}` → entry). */
  private readonly byAgent = new Map<string, Map<string, LedgerEntry>>();

  /**
   * Fold one connectivity-probe round's verdicts into the ledger. `down`
   * records (or re-arms) the dependency; `ok` marks an already-recorded one
   * recovered; anything else is left untouched (non-escalating, so it neither
   * opens nor closes an incident).
   */
  record(codeName: string, verdicts: ConnectivityVerdict[]): void {
    for (const v of verdicts) {
      if (v.status === 'down') {
        const entries = this.entriesFor(codeName);
        const key = ledgerKey(v.kind, v.label);
        const existing = entries.get(key);
        // A re-down clears a prior recovery — it's down again, not back online.
        if (existing) existing.recovered = false;
        else entries.set(key, { kind: v.kind, label: v.label, recovered: false });
      } else if (v.status === 'ok') {
        // Only deps we previously saw go down are tracked — a dep that was
        // never down has no entry and earns none here.
        const entry = this.byAgent.get(codeName)?.get(ledgerKey(v.kind, v.label));
        if (entry) entry.recovered = true;
      }
    }
  }

  /**
   * The dependencies that went down and have since read `ok` again — the
   * close-the-loop set for this agent's auto-resume. Read-only; the caller
   * {@link clear}s the agent once the resume that consumes this succeeds.
   */
  collectRecovered(codeName: string): RecoveredDependency[] {
    const entries = this.byAgent.get(codeName);
    if (!entries) return [];
    const out: RecoveredDependency[] = [];
    for (const e of entries.values()) {
      if (e.recovered) out.push({ kind: e.kind, label: e.label });
    }
    return out;
  }

  /**
   * Drop an agent's ledger — call when its trip clears so the next trip diffs
   * from a clean slate (and unrecovered/deleted deps don't linger forever).
   */
  clear(codeName: string): void {
    this.byAgent.delete(codeName);
  }

  private entriesFor(codeName: string): Map<string, LedgerEntry> {
    let entries = this.byAgent.get(codeName);
    if (!entries) {
      entries = new Map();
      this.byAgent.set(codeName, entries);
    }
    return entries;
  }
}
